/**
 * LinkDocumentDialog - manually link a bank-feed transaction to an
 * existing open document (invoice / bill / other_receipt / other_payment).
 *
 * Powered by GET /api/bank-feeds/{tx_id}/match-candidates which returns the
 * highest-likelihood matches first. We also support unlink as a destructive
 * action (separate confirmation) for previously-matched rows.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Link2,
  Search,
  Loader2,
  FileText,
  Receipt,
  ChevronRight,
  Sparkles,
  AlertCircle,
  Calendar,
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_LABEL = {
  invoice: 'Invoice',
  bill: 'Bill',
  other_receipt: 'Other Receipt',
  other_payment: 'Other Payment',
};

const DOC_ICON = {
  invoice: FileText,
  bill: FileText,
  other_receipt: Receipt,
  other_payment: Receipt,
};

const confidenceTone = (score) => {
  if (score >= 100) return { label: 'Exact match', color: 'bg-emerald-600 text-white' };
  if (score >= 80) return { label: 'Very likely', color: 'bg-emerald-100 text-emerald-700' };
  if (score >= 50) return { label: 'Possible', color: 'bg-amber-100 text-amber-700' };
  return { label: 'Weak match', color: 'bg-slate-100 text-slate-600' };
};

export const LinkDocumentDialog = ({
  open,
  onOpenChange,
  transaction,        // the full tx object
  currency = 'NGN',
  mode = 'link',      // 'link' | 'swap': swap mode swaps an auto-created
                      // doc for an existing one (deletes the auto-created
                      // one server-side, no duplicates).
  onLinked,           // callback after a successful link
}) => {
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [query, setQuery] = useState('');
  const [linking, setLinking] = useState(null); // docId currently being linked
  const txAmount = transaction ? Math.abs(transaction.amount || 0) : 0;

  useEffect(() => {
    if (!open || !transaction?.id) return;
    let cancelled = false;
    setLoading(true);
    setCandidates([]);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/bank-feeds/${transaction.id}/match-candidates?limit=25`);
        if (!cancelled) setCandidates(data.candidates || []);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || 'Could not load match candidates.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, transaction?.id]);

  const filtered = useMemo(() => {
    if (!query) return candidates;
    const q = query.toLowerCase();
    return candidates.filter((c) =>
      [c.document_number, c.counterparty, c.status].some((v) => (v || '').toLowerCase().includes(q)),
    );
  }, [candidates, query]);

  const handleLink = async (cand) => {
    setLinking(cand.id);
    try {
      // In swap mode hit the swap-link endpoint which reverses + deletes
      // the auto-created doc and links the chosen existing one in one
      // atomic operation so we never leave a duplicate.
      const endpoint = mode === 'swap'
        ? `${API}/api/bank-feeds/swap-link/${transaction.id}`
        : `${API}/api/bank-feeds/link/${transaction.id}`;
      await axios.post(endpoint, {
        document_id: cand.id,
        document_type: cand.document_type,
      });
      toast.success(
        mode === 'swap'
          ? `Swapped to ${DOC_LABEL[cand.document_type]} ${cand.document_number || ''}: auto-created doc deleted.`
          : `Linked to ${DOC_LABEL[cand.document_type]} ${cand.document_number || ''}`
      );
      await onLinked?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to link');
    } finally {
      setLinking(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="link-document-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#108981]" />
            Link to an existing document
          </DialogTitle>
          <DialogDescription className="text-sm">
            {transaction && (
              <>
                <strong>{Math.abs(transaction.amount || 0) > 0 ? formatCurrency(txAmount, currency) : '-'}</strong>{' '}
                {transaction.direction === 'inflow' ? 'inflow' : 'outflow'}
                {transaction.counterparty ? <> from/to <strong>{transaction.counterparty}</strong></> : null}.
                {' '}Pick the document this transaction belongs to. Documents already linked elsewhere are hidden.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by document number, counterparty…"
              className="pl-8"
              data-testid="link-candidates-search"
            />
          </div>

          {loading && (
            <div className="flex items-center justify-center py-10 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Finding likely matches…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground" data-testid="link-no-candidates">
              <AlertCircle className="w-6 h-6 mx-auto mb-2 opacity-60" />
              <p className="font-medium">No open documents match this transaction.</p>
              <p className="text-xs mt-1">
                Create the document first (e.g. issue an invoice), or leave the transaction in Review Needed and classify it instead.
              </p>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="max-h-[400px] overflow-y-auto border rounded-lg divide-y" data-testid="link-candidates-list">
              {filtered.map((c) => {
                const Icon = DOC_ICON[c.document_type] || FileText;
                const tone = confidenceTone(c.score);
                return (
                  <button
                    key={`${c.document_type}-${c.id}`}
                    onClick={() => handleLink(c)}
                    disabled={linking !== null}
                    className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 text-left transition-colors disabled:opacity-50"
                    data-testid={`link-candidate-${c.id}`}
                  >
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-[#108981]/10 grid place-items-center">
                      <Icon className="w-4 h-4 text-[#108981]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-sm truncate">{c.document_number || '-'}</p>
                        <Badge variant="outline" className="text-[10px]">{DOC_LABEL[c.document_type] || c.document_type}</Badge>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${tone.color}`}>
                          <Sparkles className="w-2.5 h-2.5" />
                          {tone.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        {c.counterparty && <span className="truncate max-w-[180px]">{c.counterparty}</span>}
                        {c.date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(c.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' })}
                          </span>
                        )}
                        {c.status && <Badge variant="outline" className="text-[9px]">{c.status}</Badge>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-sm tabular-nums">
                        {formatCurrency(c.outstanding, c.currency || currency)}
                      </p>
                      {c.outstanding !== c.total_amount && (
                        <p className="text-[10px] text-muted-foreground">
                          of {formatCurrency(c.total_amount, c.currency || currency)}
                        </p>
                      )}
                    </div>
                    {linking === c.id ? (
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={linking !== null} data-testid="link-dialog-close">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkDocumentDialog;
